import { useCallback } from 'react';
import { useAuth } from '@/context/auth-context';
import { useSite } from '@/contexts/SiteContext';
import { API_URL } from '@/config/api';

export function useOnboardingTracking() {
  const { user } = useAuth();
  const { selectedSite } = useSite();

  const trackOnboardingStep = useCallback(async (stepId: string, metadata?: any) => {
    if (!user || !selectedSite) {
      console.warn('Cannot track onboarding step: user or site not selected');
      return;
    }

    const siteId = selectedSite._id || selectedSite.id;

    try {
      // Map step IDs to activity types for the backend
      const activityTypeMap: Record<string, string> = {
        'talk_to_agents': 'agent_chat_started',
        'learn_analytics': 'analytics_visited',
        'watch_experiences': 'experiences_visited',
        'understand_behavior': 'behavior_visited',
        'measure_impact': 'page_visit',
        'invite_friend': 'friend_invited'
      };

      const activityType = activityTypeMap[stepId] || 'page_visit';

      console.log('📤 Sending onboarding tracking request:', {
        url: `${API_URL}/onboarding/track`,
        siteId,
        activityType,
        stepId,
        metadata
      });

      const response = await fetch(`${API_URL}/onboarding/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          siteId,
          activity: {
            type: activityType,
            stepId,
            ...metadata
          }
        })
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        console.error('❌ Tracking response error:', response.status, responseData);
        throw new Error(responseData.error || 'Failed to track onboarding step');
      }

      console.log(`✅ Onboarding step '${stepId}' tracked for site ${siteId}`, responseData);
    } catch (error) {
      console.error('Error tracking onboarding step:', error);
    }
  }, [user, selectedSite]);

  return { trackOnboardingStep };
}